import React, { useState, useRef } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

import Icon from '../../../components/AppIcon';

const IncidentForm = ({ onSubmit, onSaveDraft, isSubmitting, isDraftSaving }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    severity: '',
    impactedSystems: [],
    assignedTo: '',
    category: '',
    tags: [],
    initialResponse: '',
    attachments: [],
    priority: '',
    source: '',
    affectedUsers: '',
    estimatedImpact: '',
    containmentStatus: 'not_started'
  });

  const [errors, setErrors] = useState({});
  const [customTag, setCustomTag] = useState('');
  const fileInputRef = useRef(null);

  const severityOptions = [
    { value: 'critical', label: 'Critical', description: 'System-wide outage or data breach' },
    { value: 'high', label: 'High', description: 'Significant impact on operations' },
    { value: 'medium', label: 'Medium', description: 'Moderate impact, workaround available' },
    { value: 'low', label: 'Low', description: 'Minor impact, minimal disruption' }
  ];

  const systemOptions = [
    { value: 'web_servers', label: 'Web Servers', description: 'Public-facing web infrastructure' },
    { value: 'database', label: 'Database Systems', description: 'Core data storage systems' },
    { value: 'network', label: 'Network Infrastructure', description: 'Routers, switches, firewalls' },
    { value: 'email', label: 'Email Systems', description: 'Mail servers and services' },
    { value: 'authentication', label: 'Authentication Services', description: 'Identity and access management' },
    { value: 'backup', label: 'Backup Systems', description: 'Data backup and recovery' },
    { value: 'monitoring', label: 'Monitoring Tools', description: 'System monitoring and alerting' },
    { value: 'endpoints', label: 'Endpoint Devices', description: 'Workstations and mobile devices' }
  ];

  const assigneeOptions = [
    { value: 'john_doe', label: 'John Doe', description: 'Senior Security Analyst' },
    { value: 'sarah_wilson', label: 'Sarah Wilson', description: 'Incident Response Lead' },
    { value: 'mike_chen', label: 'Mike Chen', description: 'Network Security Specialist' },
    { value: 'lisa_garcia', label: 'Lisa Garcia', description: 'Malware Analysis Expert' },
    { value: 'david_brown', label: 'David Brown', description: 'Forensics Investigator' },
    { value: 'auto_assign', label: 'Auto-assign', description: 'Assign based on severity and workload' }
  ];

  const categoryOptions = [
    { value: 'malware', label: 'Malware/Virus' },
    { value: 'phishing', label: 'Phishing Attack' },
    { value: 'data_breach', label: 'Data Breach' },
    { value: 'ddos', label: 'DDoS Attack' },
    { value: 'unauthorized_access', label: 'Unauthorized Access' },
    { value: 'system_compromise', label: 'System Compromise' },
    { value: 'policy_violation', label: 'Policy Violation' },
    { value: 'other', label: 'Other' }
  ];

  const priorityOptions = [
    { value: 'p1', label: 'P1 - Emergency', description: 'Immediate response required' },
    { value: 'p2', label: 'P2 - High', description: 'Response within 2 hours' },
    { value: 'p3', label: 'P3 - Medium', description: 'Response within 8 hours' },
    { value: 'p4', label: 'P4 - Low', description: 'Response within 24 hours' }
  ];

  const sourceOptions = [
    { value: 'automated_alert', label: 'Automated Alert' },
    { value: 'user_report', label: 'User Report' },
    { value: 'security_scan', label: 'Security Scan' },
    { value: 'threat_intelligence', label: 'Threat Intelligence' },
    { value: 'external_report', label: 'External Report' },
    { value: 'routine_audit', label: 'Routine Audit' }
  ];

  const predefinedTags = [
    'ransomware', 'phishing', 'malware', 'ddos', 'insider-threat',
    'data-loss', 'network-intrusion', 'web-attack', 'social-engineering',
    'credential-theft', 'zero-day', 'apt', 'compliance', 'privacy'
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event?.target?.files);
    const validFiles = files?.filter(file => {
      const validTypes = ['.pdf', '.doc', '.docx', '.txt', '.jpg', '.jpeg', '.png', '.zip'];
      const fileExtension = '.' + file?.name?.split('.')?.pop()?.toLowerCase();
      return validTypes?.includes(fileExtension) && file?.size <= 10 * 1024 * 1024; // 10MB limit
    });

    setFormData(prev => ({
      ...prev,
      attachments: [...prev?.attachments, ...validFiles?.map(file => ({
        id: Date.now() + Math.random(),
        file,
        name: file?.name,
        size: file?.size,
        type: file?.type
      }))]
    }));
  };

  const removeAttachment = (attachmentId) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev?.attachments?.filter(att => att?.id !== attachmentId)
    }));
  };

  const addCustomTag = () => {
    if (customTag?.trim() && !formData?.tags?.includes(customTag?.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev?.tags, customTag?.trim()]
      }));
      setCustomTag('');
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev?.tags?.filter(tag => tag !== tagToRemove)
    }));
  };

  const togglePredefinedTag = (tag) => {
    setFormData(prev => ({
      ...prev,
      tags: prev?.tags?.includes(tag) 
        ? prev?.tags?.filter(t => t !== tag)
        : [...prev?.tags, tag]
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.title?.trim()) {
      newErrors.title = 'Incident title is required';
    } else if (formData?.title?.length < 10) {
      newErrors.title = 'Title must be at least 10 characters';
    }

    if (!formData?.description?.trim()) {
      newErrors.description = 'Incident description is required';
    } else if (formData?.description?.length < 50) {
      newErrors.description = 'Description must be at least 50 characters';
    }

    if (!formData?.severity) {
      newErrors.severity = 'Severity level is required';
    }

    if (formData?.impactedSystems?.length === 0) {
      newErrors.impactedSystems = 'At least one impacted system must be selected';
    }

    if (!formData?.category) {
      newErrors.category = 'Incident category is required';
    }

    if (!formData?.priority) {
      newErrors.priority = 'Priority level is required';
    }

    if (!formData?.source) {
      newErrors.source = 'Incident source is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const handleSaveDraft = () => {
    onSaveDraft(formData);
  };

  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'bg-error text-error-foreground',
      high: 'bg-warning text-warning-foreground',
      medium: 'bg-accent text-accent-foreground',
      low: 'bg-success text-success-foreground'
    };
    return colors?.[severity] || 'bg-muted text-muted-foreground';
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* Basic Information */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
          <Icon name="AlertTriangle" size={20} className="mr-2" />
          Basic Information
        </h3>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="lg:col-span-2">
            <Input
              label="Incident Title"
              type="text"
              placeholder="Brief, descriptive title of the incident"
              value={formData?.title}
              onChange={(e) => handleInputChange('title', e?.target?.value)}
              error={errors?.title}
              required
              maxLength={200}
            />
          </div>

          <div className="lg:col-span-2">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-foreground">
                Incident Description <span className="text-error">*</span>
              </label>
              <textarea
                className="w-full min-h-[120px] px-3 py-2 border border-input rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-vertical"
                placeholder="Detailed description of the incident, including what happened, when it was discovered, and initial observations..."
                value={formData?.description}
                onChange={(e) => handleInputChange('description', e?.target?.value)}
                maxLength={2000}
              />
              {errors?.description && (
                <p className="text-sm text-error">{errors?.description}</p>
              )}
              <p className="text-xs text-muted-foreground">
                {formData?.description?.length}/2000 characters
              </p>
            </div>
          </div>

          <Select
            label="Severity Level"
            placeholder="Select severity"
            options={severityOptions}
            value={formData?.severity}
            onChange={(value) => handleInputChange('severity', value)}
            error={errors?.severity}
            required
          />

          <Select
            label="Priority"
            placeholder="Select priority"
            options={priorityOptions}
            value={formData?.priority}
            onChange={(value) => handleInputChange('priority', value)}
            error={errors?.priority}
            required
          />

          <Select
            label="Category"
            placeholder="Select category"
            options={categoryOptions}
            value={formData?.category}
            onChange={(value) => handleInputChange('category', value)}
            error={errors?.category}
            required
          />

          <Select
            label="Incident Source"
            placeholder="How was this incident discovered?"
            options={sourceOptions}
            value={formData?.source}
            onChange={(value) => handleInputChange('source', value)}
            error={errors?.source}
            required
          />
        </div>
      </div>
      {/* Impacted Systems */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
          <Icon name="Server" size={20} className="mr-2" />
          Impacted Systems
        </h3>

        <Select
          label="Affected Systems"
          placeholder="Select all impacted systems"
          options={systemOptions}
          value={formData?.impactedSystems}
          onChange={(value) => handleInputChange('impactedSystems', value)}
          error={errors?.impactedSystems}
          multiple
          searchable
          required
        />

        <div className="mt-4">
          <Input
            label="Estimated Affected Users"
            type="number"
            placeholder="Number of users potentially affected"
            value={formData?.affectedUsers}
            onChange={(e) => handleInputChange('affectedUsers', e?.target?.value)}
            min="0"
          />
        </div>

        <div className="mt-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-foreground">
              Estimated Business Impact
            </label>
            <textarea
              className="w-full min-h-[80px] px-3 py-2 border border-input rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-vertical"
              placeholder="Describe the potential business impact, financial implications, or operational disruptions..."
              value={formData?.estimatedImpact}
              onChange={(e) => handleInputChange('estimatedImpact', e?.target?.value)}
              maxLength={500}
            />
          </div>
        </div>
      </div>
      {/* Assignment and Response */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
          <Icon name="Users" size={20} className="mr-2" />
          Assignment & Initial Response
        </h3>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Select
            label="Assign To"
            placeholder="Select assignee or auto-assign"
            options={assigneeOptions}
            value={formData?.assignedTo}
            onChange={(value) => handleInputChange('assignedTo', value)}
            searchable
          />

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Containment Status
            </label>
            <div className="space-y-2">
              {[
                { value: 'not_started', label: 'Not Started', description: 'No containment actions taken' },
                { value: 'in_progress', label: 'In Progress', description: 'Containment measures being implemented' },
                { value: 'contained', label: 'Contained', description: 'Threat has been contained' }
              ]?.map((status) => (
                <label key={status?.value} className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="containmentStatus"
                    value={status?.value}
                    checked={formData?.containmentStatus === status?.value}
                    onChange={(e) => handleInputChange('containmentStatus', e?.target?.value)}
                    className="mt-1 w-4 h-4 text-primary border-border focus:ring-ring"
                  />
                  <div>
                    <div className="text-sm font-medium text-foreground">{status?.label}</div>
                    <div className="text-xs text-muted-foreground">{status?.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-foreground">
                Initial Response Actions
              </label>
              <textarea
                className="w-full min-h-[100px] px-3 py-2 border border-input rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-vertical"
                placeholder="Document any immediate actions taken or planned, including containment measures, evidence preservation, notifications sent..."
                value={formData?.initialResponse}
                onChange={(e) => handleInputChange('initialResponse', e?.target?.value)}
                maxLength={1000}
              />
            </div>
          </div>
        </div>
      </div>
      {/* Tags and Categorization */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
          <Icon name="Tag" size={20} className="mr-2" />
          Tags & Categorization
        </h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Predefined Tags
            </label>
            <div className="flex flex-wrap gap-2">
              {predefinedTags?.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => togglePredefinedTag(tag)}
                  className={`px-3 py-1 text-sm rounded-full border transition-colors duration-200 ${
                    formData?.tags?.includes(tag)
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-background text-foreground border-border hover:bg-muted'
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Custom Tags
            </label>
            <div className="flex space-x-2">
              <Input
                type="text"
                placeholder="Add custom tag"
                value={customTag}
                onChange={(e) => setCustomTag(e?.target?.value)}
                onKeyPress={(e) => e?.key === 'Enter' && (e?.preventDefault(), addCustomTag())}
                className="flex-1"
              />
              <Button
                type="button"
                variant="outline"
                onClick={addCustomTag}
                disabled={!customTag?.trim()}
                iconName="Plus"
                iconPosition="left"
              >
                Add
              </Button>
            </div>
          </div>

          {formData?.tags?.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Selected Tags
              </label>
              <div className="flex flex-wrap gap-2">
                {formData?.tags?.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center px-3 py-1 text-sm bg-secondary text-secondary-foreground rounded-full"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      className="ml-2 hover:text-error transition-colors duration-200"
                    >
                      <Icon name="X" size={14} />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      {/* File Attachments */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
          <Icon name="Paperclip" size={20} className="mr-2" />
          Evidence & Attachments
        </h3>

        <div className="space-y-4">
          <div
            className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary transition-colors duration-200 cursor-pointer"
            onClick={() => fileInputRef?.current?.click()}
          >
            <Icon name="Upload" size={32} className="mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-foreground mb-1">
              Click to upload or drag and drop files here
            </p>
            <p className="text-xs text-muted-foreground">
              Supported: PDF, DOC, DOCX, TXT, JPG, PNG, ZIP (Max 10MB each)
            </p>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.zip"
            onChange={handleFileUpload}
            className="hidden"
          />

          {formData?.attachments?.length > 0 && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-foreground">
                Uploaded Files ({formData?.attachments?.length})
              </label>
              <div className="space-y-2">
                {formData?.attachments?.map((attachment) => (
                  <div
                    key={attachment?.id}
                    className="flex items-center justify-between p-3 bg-muted rounded-md"
                  >
                    <div className="flex items-center space-x-3">
                      <Icon name="File" size={16} className="text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium text-foreground">{attachment?.name}</p>
                        <p className="text-xs text-muted-foreground">{formatFileSize(attachment?.size)}</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeAttachment(attachment?.id)}
                      className="text-error hover:text-error/80 transition-colors duration-200"
                    >
                      <Icon name="Trash2" size={16} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Form Actions */}
      <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0 sm:space-x-4 pt-6 border-t border-border">
        <div className="flex items-center space-x-2 text-sm text-muted-foreground">
          <Icon name="Info" size={16} />
          <span>All required fields must be completed before submission</span>
        </div>

        <div className="flex space-x-3">
          <Button
            type="button"
            variant="outline"
            onClick={handleSaveDraft}
            loading={isDraftSaving}
            iconName="Save"
            iconPosition="left"
          >
            Save Draft
          </Button>
          
          <Button
            type="submit"
            variant="default"
            loading={isSubmitting}
            iconName="Send"
            iconPosition="left"
          >
            Create Incident
          </Button>
        </div>
      </div>
    </form>
  );
};

export default IncidentForm;